import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useTracking } from '../utils';
import type { InputProps } from '../input/input';
import type { TrackingProp } from '../types';
import styles from './toggles.module.css';
import clsx from 'clsx';

type Value = InputProps['value'];
export type TogglesProps = Omit<
  React.ComponentPropsWithRef<'div'>,
  'onChange'
> & {
  onChange?: (value: Value) => void;
  defaultValue?: Value;
  fill?: boolean;
  tracking?: TrackingProp;
  value?: Value;
  variant?:
    | 'blue-grouped'
    | 'white-grouped'
    | 'blue-separated'
    | 'white-separated';
};

export type ToggleProps = Omit<
  React.ComponentPropsWithRef<'button'>,
  'value'
> & {
  icon?: React.ReactNode;
  tracking?: TrackingProp;
  value: Value;
};

const TogglesContext = createContext(
  {} as {
    setValue: (value: Value, label?: string, tracking?: TrackingProp) => void;
    track: ReturnType<typeof useTracking>;
    value?: Value;
  }
);

// Make a copy of Tabs to ensure we don't overwrite the original
const TogglesComp = forwardRef<HTMLDivElement, TogglesProps>(function Toggles(
  {
    children,
    className,
    defaultValue: uncontrolled,
    fill = false,
    onChange,
    tracking = true,
    value: controlled,
    variant = 'blue-grouped',
    ...rest
  },
  ref
) {
  const [defaultValue, setDefaultValue] = useState<Value>(uncontrolled);
  const innerRef = useRef<HTMLDivElement>(null);
  const value = controlled ?? defaultValue;
  const [color, layout] = variant.split('-');
  const classes = clsx(
    className,
    styles.toggles,
    styles[color],
    styles[layout],
    !!fill && styles.fill
  );

  const track = useTracking(tracking);
  const setValue = (value: Value, label?: string, tracking?: TrackingProp) => {
    setDefaultValue(value);
    if (tracking)
      track({
        eventCategory: 'toggles',
        eventAction: 'change',
        eventLabel: `${label || value}`,
        ...(tracking === true ? {} : tracking)
      });
    onChange?.(value);
  };

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement); // Forward innerRef
  useGroupingObserver(innerRef, layout);

  return (
    <TogglesContext.Provider value={{ setValue, track, value }}>
      <div
        className={classes}
        onKeyDown={onKeyDown}
        ref={innerRef}
        role="radiogroup" // Using role="radiogroup" instead true <input type="radio"> to allow keyboard navigation without selecting
        {...rest}
      >
        {children}
      </div>
    </TogglesContext.Provider>
  );
});

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(function Tagline(
  { children, className, icon, tracking = true, onClick, ...rest },
  ref
) {
  const { setValue, value } = useContext(TogglesContext);
  const isChecked = rest.value === value;

  if (!children && !rest['aria-label'])
    console.warn('Toggle must have children or aria-label to be accessible.');

  return (
    <button
      aria-checked={isChecked}
      className={clsx(styles.toggle, className)}
      onClick={(event) => {
        const label = event.currentTarget.textContent || rest['aria-label'];
        setValue(rest.value, label, tracking);
        onClick?.(event);
      }}
      ref={ref}
      role="radio"
      tabIndex={value === undefined || isChecked ? 0 : -1} // If no value is set, allow all to be focused
      type="button"
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
});

export const Toggles = Object.assign(TogglesComp, { Toggle });

const useGroupingObserver = (ref: React.RefObject<Element>, layout: string) =>
  useEffect(() => {
    if (layout !== 'grouped' || !window.ResizeObserver || !ref.current) return;
    const self = ref.current;
    const items = self.querySelectorAll<HTMLElement>(`.${styles.toggle}`);
    const observer = new ResizeObserver(() =>
      window.requestAnimationFrame(() => {
        // Update layout after reflow to avoid infinite loop in Safari
        const isWrap = items[0].offsetTop < items[items.length - 1].offsetTop;
        self.classList.toggle(styles.grouped, !isWrap);
        self.classList.toggle(styles.separated, isWrap);
      })
    );
    observer.observe(self);
    return () => observer.disconnect();
  }, [ref, layout]);

const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
  const { currentTarget: toggles, target, key } = event;
  const items = toggles.querySelectorAll<HTMLElement>(`.${styles.toggle}`);
  const index = [...items].findIndex((item) => item.contains(target as Node));
  let next = -1;

  if (key === ' ' || key === 'Enter') items[(next = index)]?.click();
  if (key === 'ArrowLeft' || key === 'ArrowUp') next = index - 1;
  if (key === 'ArrowRight' || key === 'ArrowDown') next = index + 1;
  if (key === 'Home') next = 0;
  if (key === 'End') next = items.length - 1;
  if (items[next]) {
    event.preventDefault();
    items[next].focus();
  }
};
